/* External */
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';

/* Components */
import FocusableButton from '../../FocusableButton';

/* Utils */
import { Content, Schedule } from '../../../storage/models';
import { useOnBackPress } from '../../../utils/keyDownEvents';
import { noOp } from '../../../utils/functions';
import { useTitle } from '../../../utils/metaDataUtils';
import { redirectTo } from '../../../utils/history';
import { getPath, Routes } from '../../../utils/router';

/* Styles */
import './AreYouStillWatching.scss';

function AreYouStillWatching({
  isLive,
  content,
  schedule,
  realFocusKey,
  resumePlayback,
  isStillWatchingActive,
  setFocus,
  isFromInterstitial,
}) {
  const history = useHistory();
  const continueWatchingButtonFocusKey = `${realFocusKey}-continue`;

  useEffect(() => {
    if (isStillWatchingActive) {
      setFocus(continueWatchingButtonFocusKey);
    }
  }, [setFocus, isStillWatchingActive, continueWatchingButtonFocusKey]);

  const { formatMessage } = useIntl();
  const vodName = useTitle(content, schedule);
  const areYouStillWatchingMessage = useMemo(() => (
    isLive ? formatMessage({
      id: 'player.areYouStillWatching.title.live',
      defaultMessage: 'no está disponible en tu paquete',
    }) : formatMessage({
      id: 'player.areYouStillWatching.title.vod',
      defaultMessage: `¿Sigues viendo ${vodName}?`,
    }, { vodName })
  ), [formatMessage, isLive, vodName]);

  useOnBackPress({ listener: resumePlayback, when: isStillWatchingActive });

  const handleExitPlayer = useCallback(() => {
    if (isFromInterstitial) {
      redirectTo(getPath(Routes.Private));
    } else {
      history.goBack();
    }
  }, [history, isFromInterstitial]);

  return (
    <div className="dtv-html5-c-are-you-still-watching">
      <span className="dtv-html5-c-are-you-still-watching__title">
        {areYouStillWatchingMessage}
      </span>
      <div className="dtv-html5-c-are-you-still-watching__buttons-container">
        <FocusableButton
          onEnterPress={resumePlayback}
          focusKey={continueWatchingButtonFocusKey}
        >
          <span>
            <FormattedMessage id="player.areYouStillWatching.continue" />
          </span>
        </FocusableButton>
        <FocusableButton
          onEnterPress={handleExitPlayer}
        >
          <span>
            <FormattedMessage id="player.areYouStillWatching.exit" />
          </span>
        </FocusableButton>
      </div>
    </div>
  );
}

AreYouStillWatching.propTypes = {
  isLive: PropTypes.bool,
  content: PropTypes.instanceOf(Content),
  schedule: PropTypes.instanceOf(Schedule),
  realFocusKey: PropTypes.string,
  resumePlayback: PropTypes.func.isRequired,
  setFocus: PropTypes.func,
  isStillWatchingActive: PropTypes.bool,
  isFromInterstitial: PropTypes.bool,
};

AreYouStillWatching.defaultProps = {
  isLive: false,
  content: undefined,
  schedule: undefined,
  realFocusKey: '',
  setFocus: noOp,
  isStillWatchingActive: false,
  isFromInterstitial: false,
};

export default memo(withFocusable()(memo(AreYouStillWatching)));
